import supabase from '../config/supabase';
import config from '../config/environment';
import { buildCheckoutUrl } from '../utils/checkoutPublicLinks';
import { handleServiceError } from '../utils/errorHandler';

export interface TrackedCheckoutLink {
  url: string;
  planId: number;
  projectId: number | null;
  projectCode: string | null;
  trackedLinkId: number;
  planToken: string;
  actionToken: string | null;
  refToken: string;
  expiresAt: string;
}

interface TrackedCheckoutLinkResponse {
  success?: boolean;
  data?: Omit<TrackedCheckoutLink, 'url'>;
  error?: string;
}

class CheckoutLinkTrackingServiceClass {
  async generate(
    planId: number,
    options: { studentId?: number | null; qualificationId?: number | null } = {},
  ): Promise<TrackedCheckoutLink> {
    if (!Number.isInteger(planId) || planId <= 0) {
      throw new Error('Plano inválido.');
    }

    if (!config.supabaseFunctionsUrl) {
      throw new Error('URL das Edge Functions não configurada.');
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    try {
      const response = await fetch(`${config.supabaseFunctionsUrl}/shared-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: config.supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          action: 'generate-tracked-checkout-link',
          planId,
          ...(options.studentId ? { studentId: options.studentId } : {}),
          ...(options.qualificationId ? { qualificationId: options.qualificationId } : {}),
        }),
      });
      const body = await response.json().catch(() => ({})) as TrackedCheckoutLinkResponse;

      if (!response.ok || body.success === false || !body.data?.refToken || !body.data.planToken) {
        throw new Error(body.error || 'Não foi possível gerar o link de checkout.');
      }

      const data = body.data;
      return {
        ...data,
        url: buildCheckoutUrl(data.planToken, data.actionToken, data.refToken),
      };
    } catch (error) {
      return handleServiceError(error, 'gerar link de checkout');
    }
  }
}

export const CheckoutLinkTrackingService = new CheckoutLinkTrackingServiceClass();
